import { useEffect, useState } from 'react';
import { Box, Button, Snackbar, Alert } from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNotification } from '../hooks/useSignalR';
import { browserNotificationService } from '../services/browserNotificationService';
import { useAuth } from '@shared/auth/AuthContext';

/**
 * NotificationManager Component
 * Manages browser notifications and integrates them with SignalR real-time events
 */
export function NotificationManager() {
  const { user } = useAuth();
  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);
  const [notificationEnabled, setNotificationEnabled] = useState(false);

  useEffect(() => {
    // Check if notifications are supported and permission status
    if (browserNotificationService.isSupported()) {
      const permission = browserNotificationService.getPermission();
      setNotificationEnabled(permission === 'granted');

      // Show prompt if permission is default (not asked yet)
      if (permission === 'default') {
        setShowPermissionPrompt(true);
      }
    }
  }, []);

  const handleEnableNotifications = async () => {
    const granted = await browserNotificationService.requestPermission();
    setNotificationEnabled(granted);
    setShowPermissionPrompt(false);

    if (granted) {
      // Show a welcome notification
      browserNotificationService.showNotification('Notifications Enabled', {
        body: 'You will now receive real-time notifications for ticket updates',
        icon: '/icon-192x192.png'
      });
    }
  };

  const handleDismiss = () => {
    setShowPermissionPrompt(false);
  };

  // Listen for ticket created events (for agents/admins)
  useNotification('TicketCreated', (data: any) => {
    if (!notificationEnabled) return;
    if (user?.role !== 'Agent' && user?.role !== 'Admin') return;

    browserNotificationService.showTicketCreated(
      data.ticketNumber,
      data.subject
    );
  }, [notificationEnabled, user?.role]);

  // Listen for ticket assigned events
  useNotification('TicketAssigned', (data: any) => {
    if (!notificationEnabled) return;
    if (data.assignedTo !== user?.userId) return;

    browserNotificationService.showTicketAssigned(
      data.ticketNumber,
      'You have been assigned a new ticket'
    );
  }, [notificationEnabled, user?.userId]);

  // Listen for new message events
  useNotification('NewMessage', (data: any) => {
    if (!notificationEnabled) return;
    // Don't show notification for your own messages
    if (data.authorId === user?.userId) return;

    browserNotificationService.showNewMessage(
      data.ticketNumber,
      data.authorName,
      data.messageContent
    );
  }, [notificationEnabled, user?.userId]);

  // Listen for status change events
  useNotification('TicketStatusChanged', (data: any) => {
    if (!notificationEnabled) return;

    browserNotificationService.showTicketStatusChanged(
      data.ticketNumber,
      data.newStatus
    );
  }, [notificationEnabled]);

  // Listen for escalation events
  useNotification('TicketEscalated', (data: any) => {
    if (!notificationEnabled) return;
    if (data.escalatedTo !== user?.userId) return;

    browserNotificationService.showTicketEscalated(data.ticketNumber);
  }, [notificationEnabled, user?.userId]);

  // Listen for SLA deadline warnings
  useNotification('SlaDeadlineApproaching', (data: any) => {
    if (!notificationEnabled) return;
    if (data.assignedAgentId !== user?.userId && user?.role !== 'Admin') return;

    browserNotificationService.showSlaDeadlineApproaching(
      data.ticketNumber,
      data.minutesRemaining
    );
  }, [notificationEnabled, user?.userId, user?.role]);

  return (
    <>
      <Snackbar
        open={showPermissionPrompt}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert
          severity="info"
          icon={<NotificationsIcon />}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button color="inherit" size="small" onClick={handleEnableNotifications}>
                Enable
              </Button>
              <Button color="inherit" size="small" onClick={handleDismiss}>
                Not Now
              </Button>
            </Box>
          }
          sx={{ width: '100%' }}
        >
          Enable browser notifications to stay updated on ticket changes
        </Alert>
      </Snackbar>
    </>
  );
}
